import React from "react";
import { withStyles } from "@material-ui/styles";
import { connect } from "react-redux";
import { I18n } from "@aws-amplify/core";
import update from "immutability-helper";
import { get, flatten, sortBy, isEmpty, size, filter, includes } from "lodash";
import moment from "moment-timezone";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";

import { fetchPost } from "../../../api/fetch";
import SchedulerCalendarContainer from "../SchedulerCalendar/SchedulerCalendarContainer";
import SchedulerFilters from "../SchedulerFilters/SchedulerFilters";
import { setBreadcrumb } from "../../../actions/common";
import { calendarViews, MDC, HSP } from "../../../constants/common";
import colors from "../../../assets/style/colors";
import { map } from "lodash";
import { toTimeZone } from "../../../utils/date";
import { getLov } from "../../../utils/lov";
import {
  initiateSchedulerFilters,
  setSchedulerFilters,
} from "../../../actions/scheduler";

const styles = {
  container: {
    margin: "24px",
    boxShadow: "0px 0px 4px 0px rgb(0, 0, 0, 0.24)",
    backgroundColor: colors.white,
    borderRadius: "4px",
    height: "100%",
    maxHeight: "calc(100vh - 148px)",
  },
  content: {
    padding: "24px",
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    width: "calc(100vw - 113px)",
    maxWidth: "100%",
  },
};

const Scheduler = (props) => {
  const { classes, dispatch, type, language } = props;

  const filters = get(props, ["filters", type]);

  const [data, setData] = React.useState();
  const [isFetching, setIsFetching] = React.useState(false);

  const startDate = get(filters, ["startDate"]);

  const [scheduleTypes, setScheduleTypes] = React.useState();

  React.useEffect(() => {
    dispatch(
      setBreadcrumb([
        {
          name: I18n.get(
            type === MDC
              ? "topbar_schedulerVehicleAccess"
              : "topbar_schedulerHumanAccess"
          ),
        },
      ])
    );
    if (isEmpty(get(filters, [type]))) {
      dispatch(initiateSchedulerFilters(type));
    }
    fetchLov();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  React.useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, get(filters, ["site"])]);

  const fetchLov = async () => {
    try {
      let scheduleTypes = await getLov("scheduleTypes");
      scheduleTypes = filter(
        scheduleTypes,
        (t) =>
          t.parent_schedule_type_id === (type === HSP ? 1 : 2) &&
          t.is_schedulable
      );
      setScheduleTypes(scheduleTypes);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchData = async () => {
    if (get(filters, ["site"])) {
      setIsFetching(true);
      try {
        const data = await fetchPost("schedules/occurrences", {
          action: "read",
          filter: {
            sites: [get(filters, ["site"])],
            begin: startDate.getTime(),
            end:
              get(filters, ["calendarView"]) === calendarViews.daily
                ? startDate.getTime() + 86400000
                : startDate.getTime() + 604800000,
          },
        });
        setData(
          flatten(
            map(
              filter(data, (i) => i.active),
              (item) =>
                map(
                  filter(get(item, ["occurrences"]), (occ) => occ.active),
                  (occ) => ({
                    ...occ,
                    events: map(get(occ, ["events"]), (e) => ({
                      ...e,
                      zones: filter(
                        get(e, ["zones"]),
                        (z) => z.active && !z.deleted
                      ),
                      profiles: filter(
                        get(e, ["profiles"]),
                        (p) => p.active && !p.deleted
                      ),
                    })),
                    when: toTimeZone(get(occ, ["when"])),
                    schedule: {
                      id: item.id,
                      version: item.version,
                      name: item.name,
                      type: item.type,
                    },
                  })
                )
            )
          )
        );
      } catch (e) {
        console.log(e);
      }
      setIsFetching(false);
    }
  };

  const handleFiltersChange = (id, index, value) => {
    if (id === "site") {
      dispatch(
        setSchedulerFilters(
          type,
          update(filters, {
            [id]: { $set: value },
            zones: { $set: [] },
          })
        )
      );
    } else {
      dispatch(
        setSchedulerFilters(
          type,
          update(filters, {
            [id]: { $set: value },
          })
        )
      );
    }
  };

  const setCalendarView = (value) => {
    const newStartDate =
      value === calendarViews.daily
        ? startDate
        : new Date(
          new Date(startDate).getTime() -
          new Date(startDate).getDay() * 86400000
        );
    dispatch(
      setSchedulerFilters(
        type,
        update(filters, {
          calendarView: { $set: value },
          startDate: {
            $set: newStartDate,
          },
        })
      )
    );


  };

  const applyFilters = (data) => {
    if (get(filters, ["profile", "id"])) {
      data = filter(
        data,
        (group) =>
          size(
            filter(
              get(group, ["events"]),
              (e) =>
                size(
                  filter(
                    get(e, ["profiles"]),
                    (p) =>
                      get(p, ["profile", "id"]) ===
                      get(filters, ["profile", "id"])
                  )
                ) > 0
            )
          ) > 0
      );
    }
    if (size(get(filters, ["zones"])) > 0) {
      data = filter(
        data,
        (group) =>
          size(
            filter(
              get(group, ["events"]),
              (e) =>
                size(
                  filter(get(e, ["zones"]), (z) =>
                    includes(get(filters, ["zones"]), get(z, ["zone", "id"]))
                  )
                ) > 0
            )
          ) > 0
      );
    }
    if (size(get(filters, ["scheduleType"])) > 0) {
      data = filter(data, (group) =>
        includes(
          get(filters, ["scheduleType"]),
          get(group, ["schedule", "type", "id"])
        )
      );
    } else {
      data = filter(data, (group) =>
        includes(
          map(scheduleTypes, (t) => t.id),
          get(group, ["schedule", "type", "id"])
        )
      );
    }
    return data;
  };

  const userTimezone = moment.tz.guess();
  const timeStart = moment()
    .utc()
    .hours(0)
    .minute(0)
    .seconds(0)
    .milliseconds(0);
  const timeEnd = moment().utc().hours(0).minute(0).seconds(0).milliseconds(0);

  timeStart.hours(14);
  timeEnd.hours(16);
  const toUtc =
    moment(timeStart).tz(userTimezone).hours() -
    moment(timeStart).tz("Etc/UTC").hours();

  const timeSlots = sortBy(
    [
      {
        id: 1,
        start: {
          hours: (14 + toUtc) % 24,
          minutes: 0,
        },
        end: {
          hours: (16 + toUtc) % 24,
          minutes: 0,
        },
      },
      {
        id: 2,
        start: {
          hours: (18 + toUtc) % 24,
          minutes: 0,
        },
        end: {
          hours: (20 + toUtc) % 24,
          minutes: 0,
        },
      },
      {
        id: 3,
        start: {
          hours: (22 + toUtc) % 24,
          minutes: 0,
        },
        end: {
          hours: (24 + toUtc) % 24,
          minutes: 0,
        },
      },
    ],
    (item) => item.start.hours
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <Box className={classes.container}>
        <Grid
          container
          direction="column"
          wrap="nowrap"
          className={classes.content}
        >
          {!isEmpty(filters) && (
            <>
              <SchedulerFilters
                calendarView={get(filters, ["calendarView"])}
                setCalendarView={setCalendarView}
                startDate={startDate}
                setStartDate={(value) =>
                  handleFiltersChange("startDate", null, value)
                }
                filters={filters}
                handleFiltersChange={handleFiltersChange}
                timeSlots={timeSlots}
                refresh={fetchData}
                language={language}
                type={type}
              />
              <SchedulerCalendarContainer
                startDate={startDate}
                data={applyFilters(data)}
                isFetching={isFetching}
                view={get(filters, ["calendarView"])}
                timeSlots={timeSlots}
                refresh={fetchData}
                filters={filters}
                handleFiltersChange={handleFiltersChange}
              />
            </>
          )}
        </Grid>
      </Box>
    </DndProvider>
  );
};

const mapStateToProps = (state) => {
  return {
    filters: get(state, ["scheduler", "filters"]),
    language: get(state, ["user", "language"]),
  };
};

export default connect(mapStateToProps)(withStyles(styles)(Scheduler));
